import { useTranslate } from 'react-polyglot'
import { useIsomorphicLayoutEffect } from 'react-use'
import { SHOPIFY_CUSTOM_ATTRIBUTES, TRACKING_EVENTS } from '~/lib/constants'

import { UnlikelyCartLineInput } from '@unlikelystudio/commerce-connector'

import { useTracker } from '~/providers/TrackerProvider'

import { fromProductCardPropsToProductTrackingData } from '~/hooks/tracking/useTrackProductCardsList'
import { useGetBundleCartProps } from '~/hooks/useGetBundleCartProps'
import useLocale from '~/hooks/useLocale'

import { getIdFromGid } from '~/utils/shopify-ids'

import { SerializedProduct } from '~/data/product-card-data'

export function getLoveNoteCustomAttributes(loveNote) {
  return [
    {
      key: 'De la part de',
      value: loveNote?.signature,
    },
    {
      key: 'Message',
      value: loveNote?.note,
    },
    {
      key: '_loveNote_text',
      value: loveNote?.note,
    },
    {
      key: '_loveNote_from',
      value: loveNote?.signature,
    },
  ]
}

export function getUserLabels(previewData, activeTabLabelImage) {
  return [
    {
      key: '_rawTitle',
      value: previewData?.rawTitle,
    },
    {
      key: '_rawText',
      value: previewData?.rawSubTitle,
    },
    {
      key: '_customTitle',
      value: previewData?.formattedTitle,
    },
    {
      key: '_customText',
      value: previewData?.formattedSubTitle,
    },
    {
      key: '_customTitleSize',
      value: previewData?.titleSizeClass,
    },
    {
      key: '_customTopMargin',
      value: previewData?.topMarginClass,
    },
    {
      key: '_customUnderTitleMargin',
      value: previewData?.underTitleMarginClass,
    },
    {
      key: '_customUnderSubTitleMargin',
      value: previewData?.underSubTitleMarginClass,
    },
    {
      key: '_labelImagebase64',
      value: activeTabLabelImage,
    },
  ]
}

export function useGetAddToCartProductProps({
  userSelection,
  data,
  shopifyProduct,
  isGiftCard,
  loveNoteAttributes,
  slug,
  atelierDate,
  userLabels,
  currentVariant,
  customizationImageState,
  isMultiBundle,
}: {
  shopifyProduct?: SerializedProduct & { [key: string]: any }
  [key: string]: any
}) {
  const t = useTranslate()
  const locale = useLocale()
  const multiId = `${getIdFromGid(shopifyProduct.shopifyId)}:${Date.now()}:${
    Math.floor(Math.random() * (95000 - 10000)) + 10000
  }`
  const customization = shopifyProduct?.customization ?? null
  const { PRODUCT_DETAIL, PRODUCT_ADD_TO_CART, VIEW_ITEM_RECENTLY } =
    TRACKING_EVENTS

  const tracker = useTracker()

  const loveNoteEnabled =
    loveNoteAttributes?.signature?.length > 0 ||
    loveNoteAttributes?.note?.length > 0

  const loveNoteCustomAttributes = loveNoteEnabled
    ? getLoveNoteCustomAttributes(loveNoteAttributes)
    : []

  const getTrackingData = () => {
    return fromProductCardPropsToProductTrackingData(
      {
        name: shopifyProduct?.productName ?? null,
        categories: shopifyProduct?.categories ?? null,
        meanings: shopifyProduct?.meanings ?? null,
        shopifyId: shopifyProduct?.shopifyId ?? null,
        defaultVariant: shopifyProduct?.defaultVariant ?? null,
        price: shopifyProduct?.defaultVariant?.price ?? null,
        compareAtPrice: shopifyProduct?.defaultVariant?.compareAtPrice ?? null,
        media: shopifyProduct?.media ?? [],
        link: shopifyProduct?.link ?? null,
      },
      {},
    )
  }

  const trackingData = getTrackingData()

  const triggerEvent = (event: string) => {
    return tracker.emit<typeof event>(event, [trackingData])
  }

  useIsomorphicLayoutEffect(() => {
    const timeout = setTimeout(() => triggerEvent(PRODUCT_DETAIL), 0)

    return () => {
      triggerEvent(VIEW_ITEM_RECENTLY)
      clearTimeout(timeout)
    }
  }, [])

  const handleAddToCartTracking = () => triggerEvent(PRODUCT_ADD_TO_CART)

  const multiBundleProps: Omit<UnlikelyCartLineInput, 'quantity'> = {
    merchandiseId: shopifyProduct.defaultVariant?.id,
    attributes: [
      {
        key: '_rootMultiId',
        value: multiId,
      },
      { key: '_sku', value: shopifyProduct.defaultVariant?.sku },
      {
        key: SHOPIFY_CUSTOM_ATTRIBUTES.trackingData,
        value: JSON.stringify(trackingData),
      },
      {
        key: SHOPIFY_CUSTOM_ATTRIBUTES.tags,
        value: shopifyProduct?.tags ? JSON.stringify(shopifyProduct.tags) : '',
      },
      {
        key: '_storyblok-slug',
        value: slug,
      },
    ],
  }

  const multiSku = shopifyProduct.defaultVariant?.sku

  const isCustomizable = !!customization

  const isAvailableForSale = currentVariant?.availableForSale

  const bundlesProps = useGetBundleCartProps({
    isMultiBundle,
    data,
    userSelection,
    userLabels,
    multiId,
    multiSku,
    customizationImageState,
  })

  const mainProduct = shopifyProduct

  const totalPrice = {
    amount:
      bundlesProps.reduce((acc, curr) => {
        return (acc += curr.price.amount)
      }, 0) +
      (isMultiBundle
        ? Number(shopifyProduct.defaultVariant?.price?.amount)
        : 0),
    currencyCode: shopifyProduct.defaultVariant?.price?.currencyCode,
  }

  const multiDisabled = bundlesProps.some((bundle) => bundle.disabled === true)
  const multiAvailableForSale =
    bundlesProps.every((bundle) => bundle.availableForSale === true) &&
    shopifyProduct?.defaultVariant?.availableForSale

  return {
    onSuccess: () => {
      handleAddToCartTracking()
    },
    children: null,
    bundles: [...bundlesProps],
    price: totalPrice,
    disabled: multiDisabled,
    rootMultiBundle: isMultiBundle ? multiBundleProps : null,
    availableForSale: multiAvailableForSale,
    attributes: [
      {
        key: SHOPIFY_CUSTOM_ATTRIBUTES.trackingData,
        value: JSON.stringify(trackingData),
      },
      {
        key: SHOPIFY_CUSTOM_ATTRIBUTES.tags,
        value: shopifyProduct?.tags ? JSON.stringify(shopifyProduct.tags) : '',
      },
      {
        key: '_storyblok-slug',
        value: slug,
      },
      {
        key: '_subtitle',
        value: mainProduct?.smallDescription ?? '',
      },
      ...(isCustomizable
        ? [
            {
              key: '_is_customizable',
              value: `${isCustomizable}`,
            },
          ]
        : []),
      ...(isGiftCard
        ? [
            {
              key: '_gift_card',
              value: `true`,
            },
          ]
        : []),

      ...loveNoteCustomAttributes,
    ],
  }
}
